// src/context/forumStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Foros de demo
const initialForums = {
  'forum-prog-1': {
    id: 'forum-prog-1',
    courseId: 'intro-programacion',
    title: 'Foro General',
    description: 'Espacio para discusiones generales del curso',
    type: 'general',
    isActive: true,
    isPinned: true,
    createdAt: '2025-01-01T00:00:00Z',
  },
  'forum-prog-2': {
    id: 'forum-prog-2',
    courseId: 'intro-programacion',
    title: 'Dudas Técnicas',
    description: 'Preguntas sobre código y programación',
    type: 'qa',
    isActive: true,
    isPinned: true,
    createdAt: '2025-01-01T00:00:00Z',
  },
  'forum-prog-3': {
    id: 'forum-prog-3',
    courseId: 'intro-programacion',
    title: 'Comparte tu Proyecto',
    description: 'Muestra tus proyectos y recibe feedback',
    type: 'showcase',
    isActive: true,
    isPinned: false,
    createdAt: '2025-01-01T00:00:00Z',
  },
};

// Topics de demo
const initialTopics = {
  'topic-1': {
    id: 'topic-1',
    forumId: 'forum-prog-2',
    courseId: 'intro-programacion',
    title: '¿Cómo uso correctamente los bucles while?',
    content: 'Hola a todos, estoy teniendo problemas con los bucles while. Mi programa se queda en un bucle infinito. ¿Alguien puede ayudarme?\n\n```python\ncontador = 0\nwhile contador < 10:\n    print(contador)\n# Olvidé incrementar el contador!\n```',
    authorId: 'user-demo',
    authorName: 'Estudiante Demo',
    authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
    isPinned: false,
    isLocked: false,
    isResolved: true,
    resolvedPostId: 'post-2',
    viewCount: 45,
    likesCount: 3,
    postsCount: 3,
    lastPostAt: '2025-01-12T15:00:00Z',
    createdAt: '2025-01-10T10:00:00Z',
  },
  'topic-2': {
    id: 'topic-2',
    forumId: 'forum-prog-1',
    courseId: 'intro-programacion',
    title: '¡Bienvenidos al curso! Preséntense aquí',
    content: '¡Hola a todos! Soy la Dra. García, su instructora. Me encantaría conocerlos. Cuéntennos:\n\n- ¿De dónde son?\n- ¿Por qué quieren aprender a programar?\n- ¿Qué esperan del curso?\n\n¡Los leo!',
    authorId: 'user-prof-garcia',
    authorName: 'Dra. María García',
    authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
    isPinned: true,
    isLocked: false,
    isResolved: false,
    viewCount: 128,
    likesCount: 15,
    postsCount: 5,
    lastPostAt: '2025-01-14T09:00:00Z',
    createdAt: '2025-01-01T08:00:00Z',
  },
  'topic-3': {
    id: 'topic-3',
    forumId: 'forum-prog-3',
    courseId: 'intro-programacion',
    title: 'Mi primera calculadora en Python',
    content: '¡Hola! Quiero compartir mi primera calculadora. Acepta las 4 operaciones básicas.\n\n```python\ndef calculadora():\n    num1 = float(input("Primer número: "))\n    op = input("Operación (+, -, *, /): ")\n    num2 = float(input("Segundo número: "))\n    \n    if op == "+":\n        print(f"Resultado: {num1 + num2}")\n    elif op == "-":\n        print(f"Resultado: {num1 - num2}")\n    elif op == "*":\n        print(f"Resultado: {num1 * num2}")\n    elif op == "/":\n        if num2 != 0:\n            print(f"Resultado: {num1 / num2}")\n        else:\n            print("Error: División por cero")\n\ncalculadora()\n```\n\n¿Qué les parece? ¿Sugerencias?',
    authorId: 'user-maria',
    authorName: 'María González',
    authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
    isPinned: false,
    isLocked: false,
    isResolved: false,
    viewCount: 32,
    likesCount: 8,
    postsCount: 2,
    lastPostAt: '2025-01-13T16:00:00Z',
    createdAt: '2025-01-13T14:00:00Z',
  },
};

// Posts/respuestas de demo
const initialPosts = {
  'topic-1': [
    {
      id: 'post-1',
      topicId: 'topic-1',
      authorId: 'user-maria',
      authorName: 'María González',
      authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
      content: 'Creo que te falta incrementar el contador dentro del while. Prueba agregando `contador += 1` dentro del bucle.',
      isAnswer: false,
      likesCount: 2,
      likedBy: ['user-demo', 'user-prof-garcia'],
      createdAt: '2025-01-10T10:30:00Z',
      updatedAt: null,
    },
    {
      id: 'post-2',
      topicId: 'topic-1',
      authorId: 'user-prof-garcia',
      authorName: 'Dra. María García',
      authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
      content: '¡Exacto! María tiene razón. El problema es que nunca incrementas el contador, así que la condición `contador < 10` siempre es verdadera.\n\nLa solución correcta es:\n\n```python\ncontador = 0\nwhile contador < 10:\n    print(contador)\n    contador += 1  # ¡Esto es lo que faltaba!\n```\n\n**Tip:** Siempre asegúrate de que tu condición de salida se cumpla en algún momento.',
      isAnswer: true,
      likesCount: 5,
      likedBy: ['user-demo', 'user-maria', 'user-carlos'],
      createdAt: '2025-01-10T11:00:00Z',
      updatedAt: null,
    },
    {
      id: 'post-3',
      topicId: 'topic-1',
      authorId: 'user-demo',
      authorName: 'Estudiante Demo',
      authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
      content: '¡Muchas gracias a ambas! Ya funciona perfectamente. 🎉',
      isAnswer: false,
      likesCount: 1,
      likedBy: ['user-prof-garcia'],
      createdAt: '2025-01-12T15:00:00Z',
      updatedAt: null,
    },
  ],
  'topic-2': [
    {
      id: 'post-4',
      topicId: 'topic-2',
      authorId: 'user-demo',
      authorName: 'Estudiante Demo',
      authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
      content: '¡Hola! Soy de Colombia 🇨🇴. Quiero aprender a programar para crear mis propias aplicaciones. Espero poder hacer un juego al final del curso!',
      isAnswer: false,
      likesCount: 3,
      likedBy: ['user-prof-garcia', 'user-maria'],
      createdAt: '2025-01-02T10:00:00Z',
      updatedAt: null,
    },
    {
      id: 'post-5',
      topicId: 'topic-2',
      authorId: 'user-maria',
      authorName: 'María González',
      authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
      content: '¡Hola a todos! Soy María de México 🇲🇽. Trabajo en marketing y quiero aprender programación para automatizar reportes. ¡Emocionada de estar aquí!',
      isAnswer: false,
      likesCount: 4,
      likedBy: ['user-prof-garcia', 'user-demo', 'user-carlos'],
      createdAt: '2025-01-03T14:00:00Z',
      updatedAt: null,
    },
  ],
  'topic-3': [
    {
      id: 'post-6',
      topicId: 'topic-3',
      authorId: 'user-prof-garcia',
      authorName: 'Dra. María García',
      authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
      content: '¡Excelente trabajo María! 👏 Tu código es limpio y maneja bien el caso de división por cero.\n\n**Sugerencias para mejorar:**\n1. Podrías usar un diccionario para las operaciones\n2. Agregar un bucle para permitir múltiples cálculos\n3. Validar que el usuario ingrese números válidos\n\n¡Sigue así!',
      isAnswer: false,
      likesCount: 3,
      likedBy: ['user-maria', 'user-demo'],
      createdAt: '2025-01-13T16:00:00Z',
      updatedAt: null,
    },
  ],
};

// Likes de usuarios
const initialUserLikes = {
  'user-demo': ['topic-2', 'post-2'],
  'user-maria': ['topic-2', 'post-2'],
  'user-prof-garcia': ['topic-3', 'post-4', 'post-5'],
};

const useForumStore = create(
  persist(
    (set, get) => ({
      forums: { ...initialForums },
      topics: { ...initialTopics },
      posts: { ...initialPosts },
      userLikes: { ...initialUserLikes },

      // ========== FORUMS ==========
      
      // Obtener foros de un curso
      getCourseForums: (courseId) => {
        const { forums } = get();
        return Object.values(forums)
          .filter(f => f.courseId === courseId && f.isActive)
          .sort((a, b) => b.isPinned - a.isPinned);
      },

      // Crear foro (instructor/admin)
      createForum: (forumData) => {
        const newForum = {
          id: `forum-${Date.now()}`,
          ...forumData,
          isActive: true,
          isPinned: false,
          createdAt: new Date().toISOString(),
        };
        set(state => ({
          forums: { ...state.forums, [newForum.id]: newForum },
        }));
        return newForum;
      },

      // Actualizar foro
      updateForum: (forumId, updates) => {
        set(state => ({
          forums: {
            ...state.forums,
            [forumId]: { ...state.forums[forumId], ...updates },
          },
        }));
      },

      // Eliminar/desactivar foro
      deleteForum: (forumId) => {
        set(state => ({
          forums: {
            ...state.forums,
            [forumId]: { ...state.forums[forumId], isActive: false },
          },
        }));
      },

      // ========== TOPICS ==========

      // Obtener topics de un foro
      getForumTopics: (forumId) => {
        const { topics } = get();
        return Object.values(topics)
          .filter(t => t.forumId === forumId)
          .sort((a, b) => {
            if (a.isPinned !== b.isPinned) return b.isPinned - a.isPinned;
            return new Date(b.lastPostAt) - new Date(a.lastPostAt);
          });
      },

      // Obtener topic por ID
      getTopicById: (topicId) => {
        return get().topics[topicId];
      },

      // Crear topic
      createTopic: (topicData, author) => {
        const newTopic = {
          id: `topic-${Date.now()}`,
          ...topicData,
          authorId: author.id,
          authorName: author.name,
          authorAvatar: author.avatar,
          isPinned: false,
          isLocked: false,
          isResolved: false,
          resolvedPostId: null,
          viewCount: 0,
          likesCount: 0,
          postsCount: 0,
          lastPostAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        };
        set(state => ({
          topics: { ...state.topics, [newTopic.id]: newTopic },
          posts: { ...state.posts, [newTopic.id]: [] },
        }));
        return newTopic;
      },

      // Actualizar topic
      updateTopic: (topicId, updates) => {
        set(state => ({
          topics: {
            ...state.topics,
            [topicId]: { ...state.topics[topicId], ...updates },
          },
        }));
      },

      // Incrementar vistas
      incrementViewCount: (topicId) => {
        set(state => ({
          topics: {
            ...state.topics,
            [topicId]: {
              ...state.topics[topicId],
              viewCount: (state.topics[topicId]?.viewCount || 0) + 1,
            },
          },
        }));
      },

      // Fijar/desfijar topic
      togglePinTopic: (topicId) => {
        set(state => ({
          topics: {
            ...state.topics,
            [topicId]: {
              ...state.topics[topicId],
              isPinned: !state.topics[topicId].isPinned,
            },
          },
        }));
      },

      // Bloquear/desbloquear topic
      toggleLockTopic: (topicId) => {
        set(state => ({
          topics: {
            ...state.topics,
            [topicId]: {
              ...state.topics[topicId],
              isLocked: !state.topics[topicId].isLocked,
            },
          },
        }));
      },

      // Marcar como resuelto
      markAsResolved: (topicId, postId) => {
        set(state => ({
          topics: {
            ...state.topics,
            [topicId]: {
              ...state.topics[topicId],
              isResolved: true,
              resolvedPostId: postId,
            },
          },
        }));
      },

      // Eliminar topic
      deleteTopic: (topicId) => {
        set(state => {
          const { [topicId]: deleted, ...restTopics } = state.topics;
          const { [topicId]: deletedPosts, ...restPosts } = state.posts;
          return { topics: restTopics, posts: restPosts };
        });
      },

      // ========== POSTS ==========

      // Obtener posts de un topic
      getTopicPosts: (topicId) => {
        return get().posts[topicId] || [];
      },

      // Crear post/respuesta
      createPost: (topicId, content, author) => {
        const { topics } = get();
        const topic = topics[topicId];
        if (!topic || topic.isLocked) return null;

        const newPost = {
          id: `post-${Date.now()}`,
          topicId,
          authorId: author.id,
          authorName: author.name,
          authorAvatar: author.avatar,
          content,
          isAnswer: false,
          likesCount: 0,
          likedBy: [],
          createdAt: new Date().toISOString(),
          updatedAt: null,
        };

        set(state => ({
          posts: {
            ...state.posts,
            [topicId]: [...(state.posts[topicId] || []), newPost],
          },
          topics: {
            ...state.topics,
            [topicId]: {
              ...state.topics[topicId],
              postsCount: (state.topics[topicId]?.postsCount || 0) + 1,
              lastPostAt: newPost.createdAt,
            },
          },
        }));

        return newPost;
      },

      // Editar post
      updatePost: (topicId, postId, content) => {
        set(state => ({
          posts: {
            ...state.posts,
            [topicId]: state.posts[topicId].map(p =>
              p.id === postId
                ? { ...p, content, updatedAt: new Date().toISOString() }
                : p
            ),
          },
        }));
      },

      // Eliminar post
      deletePost: (topicId, postId) => {
        set(state => ({
          posts: {
            ...state.posts,
            [topicId]: state.posts[topicId].filter(p => p.id !== postId),
          },
          topics: {
            ...state.topics,
            [topicId]: {
              ...state.topics[topicId],
              postsCount: Math.max(0, (state.topics[topicId]?.postsCount || 1) - 1),
            },
          },
        }));
      },

      // Marcar post como respuesta correcta
      markPostAsAnswer: (topicId, postId) => {
        set(state => ({
          posts: {
            ...state.posts,
            [topicId]: state.posts[topicId].map(p => ({
              ...p,
              isAnswer: p.id === postId,
            })),
          },
          topics: {
            ...state.topics,
            [topicId]: {
              ...state.topics[topicId],
              isResolved: true,
              resolvedPostId: postId,
            },
          },
        }));
      },

      // ========== LIKES ==========

      // Toggle like en topic
      toggleTopicLike: (topicId, userId) => {
        const { topics, userLikes } = get();
        const topic = topics[topicId];
        const likes = userLikes[userId] || [];
        const hasLiked = likes.includes(topicId);

        set(state => ({
          topics: {
            ...state.topics,
            [topicId]: {
              ...topic,
              likesCount: hasLiked ? topic.likesCount - 1 : topic.likesCount + 1,
            },
          },
          userLikes: {
            ...state.userLikes,
            [userId]: hasLiked
              ? likes.filter(id => id !== topicId)
              : [...likes, topicId],
          },
        }));
      },

      // Toggle like en post
      togglePostLike: (topicId, postId, userId) => {
        set(state => ({
          posts: {
            ...state.posts,
            [topicId]: state.posts[topicId].map(p => {
              if (p.id !== postId) return p;
              const hasLiked = p.likedBy.includes(userId);
              return {
                ...p,
                likesCount: hasLiked ? p.likesCount - 1 : p.likesCount + 1,
                likedBy: hasLiked
                  ? p.likedBy.filter(id => id !== userId)
                  : [...p.likedBy, userId],
              };
            }),
          },
        }));
      },

      // Verificar si usuario dio like
      hasUserLikedTopic: (topicId, userId) => {
        const { userLikes } = get();
        return (userLikes[userId] || []).includes(topicId);
      },

      hasUserLikedPost: (topicId, postId, userId) => {
        const { posts } = get();
        const post = (posts[topicId] || []).find(p => p.id === postId);
        return post?.likedBy?.includes(userId) || false;
      },

      // ========== ESTADÍSTICAS ==========

      getForumStats: (courseId) => {
        const { forums, topics, posts } = get();
        const courseForums = Object.values(forums).filter(f => f.courseId === courseId);
        const courseTopics = Object.values(topics).filter(t => t.courseId === courseId);
        const totalPosts = courseTopics.reduce((sum, t) => sum + (t.postsCount || 0), 0);

        return {
          forumsCount: courseForums.length,
          topicsCount: courseTopics.length,
          postsCount: totalPosts,
          resolvedCount: courseTopics.filter(t => t.isResolved).length,
        };
      },
    }),
    {
      name: 'educat-forum-v1',
    }
  )
);

export default useForumStore;
